import React, { useState, useCallback, useMemo } from "react";
import { useSubscription } from "../../../contexts/SubscriptionContext";
import { useTenantContext } from "../../../contexts/TenantContext";
import { useAuth } from "../../../contexts/AuthContext";
import { subscriptionApi } from "../../../services/api";

/** Tiered pricing bands — rate applies only to employees within each tier range. */
const PRICING_TIERS = [
  { range: "1–200",    from: 1,    to: 200,      price: 100 },
  { range: "201–400",  from: 201,  to: 400,      price: 90  },
  { range: "401–600",  from: 401,  to: 600,      price: 80  },
  { range: "601–800",  from: 601,  to: 800,      price: 70  },
  { range: "801–1000", from: 801,  to: 1000,     price: 60  },
  { range: "1001+",    from: 1001, to: Infinity,  price: 50  },
];

/** Progressive tiered total — no cliff. Each tier only covers its slice. */
function calcTieredTotal(count) {
  let total = 0;
  let remaining = count;
  for (const tier of PRICING_TIERS) {
    if (remaining <= 0) break;
    const size = tier.to === Infinity ? remaining : Math.min(remaining, tier.to - tier.from + 1);
    total += size * tier.price;
    remaining -= size;
  }
  return total;
}

/**
 * Pricing Calculator component
 */
const PricingCalculator = ({ currencySymbol = "£" }) => {
  const [employeeCount, setEmployeeCount] = useState(50);

  const { pricePerEmployee, monthlyTotal, band, tierBreakdown } = useMemo(() => {
    const tier = PRICING_TIERS.find((t) => employeeCount <= t.to) ?? PRICING_TIERS[PRICING_TIERS.length - 1];
    const total = calcTieredTotal(employeeCount);

    // Build per-tier breakdown for the table
    const breakdown = [];
    let remaining = employeeCount;
    for (const t of PRICING_TIERS) {
      if (remaining <= 0) break;
      const size = t.to === Infinity ? remaining : Math.min(remaining, t.to - t.from + 1);
      breakdown.push({ range: t.range, count: size, price: t.price, subtotal: size * t.price });
      remaining -= size;
    }

    return {
      pricePerEmployee: tier.price,
      monthlyTotal: total,
      band: tier.range,
      tierBreakdown: breakdown,
    };
  }, [employeeCount]);

  const formatMinor = (minor) => {
    return `${currencySymbol}${(minor / 100).toFixed(2)}`;
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        💰 Pro Plan Pricing Calculator
      </h3>

      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Number of employees: <strong>{employeeCount}</strong>
        </label>
        <input
          type="range"
          min="1"
          max="1500"
          value={employeeCount}
          onChange={(e) => setEmployeeCount(parseInt(e.target.value))}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
        />
        <div className="flex justify-between text-xs text-gray-400 mt-1">
          <span>1</span>
          <span>200</span>
          <span>400</span>
          <span>600</span>
          <span>800</span>
          <span>1000</span>
          <span>1500</span>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="bg-blue-50 rounded-lg p-3 text-center">
          <div className="text-xs text-blue-600 font-medium">Per Employee</div>
          <div className="text-xl font-bold text-blue-900">
            {formatMinor(pricePerEmployee)}
          </div>
          <div className="text-xs text-blue-500">/month</div>
        </div>
        <div className="bg-green-50 rounded-lg p-3 text-center">
          <div className="text-xs text-green-600 font-medium">Band</div>
          <div className="text-xl font-bold text-green-900">{band}</div>
          <div className="text-xs text-green-500">employees</div>
        </div>
        <div className="bg-indigo-50 rounded-lg p-3 text-center">
          <div className="text-xs text-indigo-600 font-medium">
            Monthly Total
          </div>
          <div className="text-xl font-bold text-indigo-900">
            {formatMinor(monthlyTotal)}
          </div>
          <div className="text-xs text-indigo-500">/month</div>
        </div>
      </div>

      {/* Tiered cost breakdown table */}
      <div className="text-xs text-gray-500">
        <p className="mb-2 text-gray-400">Each tier's rate applies only to employees in that range — no price cliff.</p>
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="py-1 text-left font-medium">Tier</th>
              <th className="py-1 text-center font-medium">Employees</th>
              <th className="py-1 text-center font-medium">Rate</th>
              <th className="py-1 text-right font-medium">Tier cost</th>
            </tr>
          </thead>
          <tbody>
            {tierBreakdown.map(({ range, count, price, subtotal }) => (
              <tr
                key={range}
                className={`border-b border-gray-50 ${
                  band === range ? "bg-blue-50 font-semibold text-blue-900" : ""
                }`}
              >
                <td className="py-1">{range}</td>
                <td className="py-1 text-center">{count}</td>
                <td className="py-1 text-center">{formatMinor(price)}/mo</td>
                <td className="py-1 text-right">{formatMinor(subtotal)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/**
 * Subscription Settings page – view plan, manage region, upgrade
 */
const SubscriptionSettings = () => {
  const {
    subscription,
    features,
    loading,
    isFreeTier,
    isProTier,
    isEnterpriseTier,
    isTrial,
    trialDaysRemaining,
    currency,
    refreshSubscription,
  } = useSubscription();
  const { } = useTenantContext();
  const { isTenantAdmin: isAdmin, isGlobalAdmin } = useAuth();

  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const handleUpgrade = useCallback(async () => {
    if (
      !window.confirm(
        "Upgrade to Pro? Your billing will start based on your current employee count.",
      )
    )
      return;
    setUpgradeLoading(true);
    setMessage(null);
    try {
      await subscriptionApi.upgradeToPro();
      await refreshSubscription();
      setMessage({ type: "success", text: "Upgraded to Pro successfully!" });
    } catch (err) {
      setMessage({
        type: "error",
        text: err?.message || "Failed to upgrade.",
      });
    } finally {
      setUpgradeLoading(false);
    }
  }, [refreshSubscription]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  const formatMinor = (minor) =>
    `${currency.symbol}${(minor / 100).toFixed(2)}`;
  const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString("en-GB", { dateStyle: "medium" }) : "—";

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Subscription</h1>
        <p className="text-gray-500 mt-1">
          Manage your plan, region, and billing
        </p>
      </div>

      {/* Messages */}
      {message && (
        <div
          className={`p-3 rounded-lg text-sm ${message.type === "success" ? "bg-green-50 text-green-800 border border-green-200" : "bg-red-50 text-red-800 border border-red-200"}`}
        >
          {message.text}
        </div>
      )}

      {/* Trial Banner */}
      {isTrial && trialDaysRemaining !== null && (
        <div
          className={`rounded-xl p-4 flex items-center gap-3 ${trialDaysRemaining <= 2 ? "bg-amber-50 border border-amber-200" : "bg-blue-50 border border-blue-200"}`}
        >
          <span className="text-2xl">
            {trialDaysRemaining <= 2 ? "⚠️" : "🎉"}
          </span>
          <div>
            <div className="font-semibold text-gray-900">
              {trialDaysRemaining > 0
                ? `${trialDaysRemaining} day${trialDaysRemaining !== 1 ? "s" : ""} left in your free trial`
                : "Your free trial has expired"}
            </div>
            <div className="text-sm text-gray-600">
              {trialDaysRemaining > 0
                ? "You have access to all Pro features. Upgrade to keep them after your trial ends."
                : "Upgrade to Pro to regain access to integrations, sync, and advanced features."}
            </div>
          </div>
          {isFreeTier && (isAdmin || isGlobalAdmin) && (
            <button
              onClick={handleUpgrade}
              disabled={upgradeLoading}
              className="ml-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap font-medium text-sm"
            >
              {upgradeLoading ? "Upgrading..." : "Upgrade to Pro"}
            </button>
          )}
        </div>
      )}

      {/* Current Plan */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Current Plan
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wide">
              Plan
            </div>
            <div className="mt-1 font-semibold text-gray-900 flex items-center gap-2">
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  isEnterpriseTier
                    ? "bg-purple-100 text-purple-800"
                    : isProTier
                      ? "bg-blue-100 text-blue-800"
                      : "bg-gray-100 text-gray-800"
                }`}
              >
                {subscription?.effectiveTier || "Free"}
              </span>
              {isTrial && (
                <span className="text-xs text-amber-600 font-normal">
                  (Trial)
                </span>
              )}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wide">
              Status
            </div>
            <div className="mt-1 font-semibold text-gray-900">
              {subscription?.status || "—"}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wide">
              Employees
            </div>
            <div className="mt-1 font-semibold text-gray-900">
              {subscription?.billableEmployeeCount ?? 0}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wide">
              Monthly Cost
            </div>
            <div className="mt-1 font-semibold text-gray-900">
              {subscription?.monthlyTotalMinor
                ? formatMinor(subscription.monthlyTotalMinor)
                : `${currency.symbol}0.00`}
            </div>
          </div>
        </div>

        {/* Billing period */}
        {subscription?.billingPeriodStart && (
          <div className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-6 text-sm text-gray-600">
            <span>
              Billing period: {formatDate(subscription.billingPeriodStart)} –{" "}
              {formatDate(subscription.billingPeriodEnd)}
            </span>
            {subscription?.pricePerEmployeeMinor > 0 && (
              <span>
                Rate: {formatMinor(subscription.pricePerEmployeeMinor)}/employee/mo
              </span>
            )}
          </div>
        )}

        {/* Upgrade CTA for Free tier */}
        {isFreeTier && !isTrial && (isAdmin || isGlobalAdmin) && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <button
              onClick={handleUpgrade}
              disabled={upgradeLoading}
              className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
            >
              {upgradeLoading ? "Upgrading..." : "Upgrade to Pro →"}
            </button>
          </div>
        )}
      </div>

      {/* Feature Access */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Feature Access
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {Object.entries(features).map(([feature, enabled]) => (
            <div
              key={feature}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${enabled ? "bg-green-50 text-green-800" : "bg-gray-50 text-gray-400"}`}
            >
              {enabled ? (
                <svg
                  className="w-4 h-4 text-green-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              ) : (
                <svg
                  className="w-4 h-4 text-gray-300"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
              <span className="capitalize">
                {feature.replace(/([A-Z])/g, " $1").trim()}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Limits */}
      {subscription && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Plan Limits
          </h2>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-gray-900">
                {subscription.maxLocations >= 999
                  ? "∞"
                  : subscription.maxLocations}
              </div>
              <div className="text-xs text-gray-500 mt-1">Locations</div>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-gray-900">
                {subscription.maxEmployees >= 10000
                  ? "∞"
                  : subscription.maxEmployees?.toLocaleString()}
              </div>
              <div className="text-xs text-gray-500 mt-1">Employees</div>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-gray-900">
                {subscription.maxUsers >= 100 ? "∞" : subscription.maxUsers}
              </div>
              <div className="text-xs text-gray-500 mt-1">Users</div>
            </div>
          </div>
        </div>
      )}

      {/* Pricing Calculator */}
      {(isProTier || isFreeTier) && (
        <PricingCalculator currencySymbol={currency.symbol} />
      )}
    </div>
  );
};

export default SubscriptionSettings;
